import React from 'react';
import { Typography, Button, Tooltip } from '@mui/material';
import PropTypes from "prop-types";
import { makeStyles } from '@mui/styles';
import moment from "moment";
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

const useItemStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    width: '100%',
    boxSizing: 'border-box',
    background: '#ffffff',
    border: '1px solid #e8eaed',
    borderRadius: 12,
    padding: '12px 14px',
    marginBottom: 10,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    transition: 'box-shadow .15s ease, border-color .15s ease',
    '&:hover': {
      boxShadow: '0 6px 18px rgba(16,24,40,0.10)',
      borderColor: '#dfe3e8',
    },
  },
}));

function ItemMenu(props) {
  const intl = props.intl;
  const item = props.item || {};
  const cls = useItemStyles();

  const required = !!item.required;
  const isSurvey = String(item.notificationType) === '14';

  // Paleta: vermelho p/ "ação necessária" (required), verde Mexx no fluxo normal.
  const accent = required ? '#E5533C' : '#8BC34A';
  const accentSoft = required ? 'rgba(229,83,60,0.12)' : 'rgba(139,195,74,0.16)';

  // Ícone por natureza da notificação.
  const TypeIcon = required
    ? WarningAmberRoundedIcon
    : (isSurvey ? RateReviewRoundedIcon : NotificationsActiveRoundedIcon);

  // Título por tipo (i18n) — sem texto hardcoded.
  const titleId = required
    ? 'notification.card.title.action.required'
    : (isSurvey ? 'notification.card.title.survey' : 'notification.card.title.default');
  const title = intl.formatMessage({ id: titleId });

  const info = item.information ?? '';
  const created = item.createdAt ? moment(item.createdAt) : null;
  const relative = created && created.isValid() ? created.fromNow() : '';
  const fullDate = created && created.isValid() ? created.format('DD/MM/YYYY HH:mm') : '';

  return (
    <div className={cls.card} style={{ borderLeft: `4px solid ${accent}` }}>
      {/* Avatar do tipo */}
      <div
        style={{
          flex: '0 0 auto',
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: accentSoft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <TypeIcon style={{ color: accent, fontSize: 20 }} />
      </div>

      {/* Conteúdo */}
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <Typography style={{ fontSize: 14, fontWeight: 700, color: '#1f2430', lineHeight: 1.3 }}>
            {title}
          </Typography>
          {relative && (
            <Tooltip title={fullDate} arrow placement="top">
              <span style={{ flex: '0 0 auto', fontSize: 11, color: '#98a2b3', whiteSpace: 'nowrap' }}>
                {relative}
              </span>
            </Tooltip>
          )}
        </div>

        <Tooltip title={info} arrow placement="top">
          <Typography
            style={{
              fontSize: 13,
              color: '#5b6472',
              marginTop: 3,
              lineHeight: 1.45,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {info}
          </Typography>
        </Tooltip>

        {/* Ações */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 8 }}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => props.redirectNotification(item)}
            endIcon={<ArrowForwardRoundedIcon style={{ fontSize: 16 }} />}
            style={{
              backgroundColor: accent,
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 8,
              padding: '4px 12px',
              boxShadow: 'none',
            }}
          >
            {intl.formatMessage({ id: 'notification.view' })}
          </Button>

          <Button
            size="small"
            variant="text"
            onClick={() => props.readNotification(item)}
            startIcon={<CheckRoundedIcon style={{ fontSize: 16 }} />}
            style={{
              color: '#667085',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              minWidth: 0,
            }}
          >
            {intl.formatMessage({ id: 'notification.mark.as.read' })}
          </Button>
        </div>
      </div>
    </div>
  );
}

ItemMenu.propTypes = {
  item: PropTypes.object.isRequired,
  readNotification: PropTypes.func,
  redirectNotification: PropTypes.func,
};

export default ItemMenu;
